import type {ReactElement} from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {EmbeddedCheckout, EmbeddedCheckoutProvider} from '@stripe/react-stripe-js';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import type {CheckoutStepProps} from '@Components/checkout-flow/checkout-flow.types';
import {CHECKOUT_PROMO_INPUT_REVEAL_DELAY_MS, CheckoutMode, CheckoutStep} from '@Components/checkout-flow/checkout-flow.types';
import {createOneTimePaymentCheckoutSession, createSubscriptionCheckoutSession} from '@Components/checkout-flow/checkout-flow-thunk';
import {getStripePromise} from '@Components/checkout-flow/stripe-loader.library';
import {PromoCodeInput} from '@Components/checkout-flow/components/promo-code-input/promo-code-input';
import {PromoCodePill} from '@Components/checkout-flow/components/promo-code-pill/promo-code-pill';
import {STRIPE_EMBEDDED_CHECKOUT_ELEMENT_ID} from '@Components/checkout-flow/components/stripe-embedded-step/stripe-embedded-step.types';
import styles from './stripe-embedded-step.module.scss';

export function StripeEmbeddedStep({request, controller}: CheckoutStepProps): ReactElement {
  const dispatch = useAppDispatch();
  const stripeSession = useAppSelector((state) => state.checkoutFlow.stripeSession);
  const dialogData = useAppSelector((state) => state.checkoutFlow.dialogData);
  const promo = useAppSelector((state) => state.checkoutFlow.promo);
  const [isPromoRevealed, setIsPromoRevealed] = useState(false);
  const [isPromoInputInView, setIsPromoInputInView] = useState(true);
  const promoContainerRef = useRef<HTMLDivElement>(null);
  const isCreatingSessionRef = useRef(false);
  const showsPromo = request.mode === CheckoutMode.SUBSCRIPTION || request.mode === CheckoutMode.DOWNLOAD;
  const usesRedirectReturnFlow = dialogData?.usesRedirectReturnFlow ?? false;

  useEffect(() => {
    if (stripeSession !== null || isCreatingSessionRef.current) {
      return;
    }
    isCreatingSessionRef.current = true;
    void createSession().finally(() => {
      isCreatingSessionRef.current = false;
    });
  }, [stripeSession]);

  useEffect(() => {
    if (!showsPromo || stripeSession === null) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setIsPromoRevealed(true);
    }, CHECKOUT_PROMO_INPUT_REVEAL_DELAY_MS);
    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [showsPromo, stripeSession]);

  useEffect(() => {
    const promoContainer = promoContainerRef.current;
    if (!isPromoRevealed || promoContainer === null) {
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      setIsPromoInputInView(entry.isIntersecting);
    });
    observer.observe(promoContainer);
    return (): void => {
      observer.disconnect();
    };
  }, [isPromoRevealed, promo.isApplied]);

  const createSession = async (): Promise<void> => {
    try {
      if (request.mode === CheckoutMode.SUBSCRIPTION) {
        await dispatch(createSubscriptionCheckoutSession({request})).unwrap();
      } else if (request.mode === CheckoutMode.CREDIT_PACK || request.mode === CheckoutMode.DOWNLOAD) {
        await dispatch(createOneTimePaymentCheckoutSession({request})).unwrap();
      }
    } catch {
      controller.fail(window.i18next.t('pmwjs_something_went_wrong'), window.i18next.t('pmwjs_error_processing_request_reload_try_again'));
    }
  };

  const onStripeCheckoutComplete = useCallback((): void => {
    controller.goToStep(CheckoutStep.FINALIZING);
  }, [controller]);

  const embeddedCheckoutOptions = useMemo(() => {
    return {clientSecret: stripeSession?.clientSecret ?? '', onComplete: usesRedirectReturnFlow ? undefined : onStripeCheckoutComplete};
  }, [stripeSession?.clientSecret, usesRedirectReturnFlow, onStripeCheckoutComplete]);

  const scrollToPromoInput = (): void => {
    promoContainerRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
  };

  const getEmbeddedCheckout = (): ReactElement => {
    if (stripeSession === null || stripeSession.clientSecret === '') {
      return (
        <div className={styles.loader}>
          <CircularProgressLoader size={LOADER_SIZE.LARGE} classes="_unmargin" />
        </div>
      );
    }
    return (
      <EmbeddedCheckoutProvider key={stripeSession.clientSecret} stripe={getStripePromise()} options={embeddedCheckoutOptions}>
        <EmbeddedCheckout id={STRIPE_EMBEDDED_CHECKOUT_ELEMENT_ID} className={styles.embeddedCheckout} />
      </EmbeddedCheckoutProvider>
    );
  };

  const getPromoSection = (): ReactElement | null => {
    if (!showsPromo || !isPromoRevealed) {
      return null;
    }
    const isPillVisible = !promo.isApplied && !isPromoInputInView;
    return (
      <>
        <div className={styles.promoContainer}>
          <PromoCodeInput ref={promoContainerRef} request={request} controller={controller} />
        </div>
        <PromoCodePill isVisible={isPillVisible} onClick={scrollToPromoInput} />
      </>
    );
  };

  return (
    <div className={styles.container}>
      {getEmbeddedCheckout()}
      {getPromoSection()}
    </div>
  );
}
